/**
 * CommentsList Component
 *
 * Displays paginated list of comments with user details.
 * Highlights @mentions in comment content.
 * Shows relative timestamps (e.g., "5m ago", "2h ago").
 */

import { useThemeColors } from '@/styles';
import { spacing, sizing, typography } from '@/styles/design-tokens';
import { Icon, IconName } from '@/icons';
import { AsyncButton } from '@/components/ui';
import type { CommentWithUserData } from '@/types/action-outputs';
import { formatDate } from '@/utils/date';

interface CommentsListProps {
  comments: CommentWithUserData[];
  isLoading: boolean;
  hasMore: boolean;
  onLoadMore?: () => Promise<void>;
}

/**
 * Parse comment content and highlight @mentions
 */
function parseCommentContent(
  content: string,
  primaryColor: string
): React.ReactNode[] {
  const mentionRegex = /@([a-zA-Z0-9]+)/g;
  const parts: React.ReactNode[] = [];
  let lastIndex = 0;
  let match;

  while ((match = mentionRegex.exec(content)) !== null) {
    // Add text before mention
    if (match.index > lastIndex) {
      parts.push(content.substring(lastIndex, match.index));
    }

    // Add highlighted mention
    parts.push(
      <span
        key={match.index}
        style={{
          color: primaryColor,
          fontWeight: 500,
        }}
      >
        {match[0]}
      </span>
    );

    lastIndex = match.index + match[0].length;
  }

  // Add remaining text
  if (lastIndex < content.length) {
    parts.push(content.substring(lastIndex));
  }

  return parts;
}

/**
 * Format timestamp to relative time
 */
function formatRelativeTime(timestamp: string): string {
  const now = new Date();
  const commentTime = new Date(timestamp);
  const diffMs = now.getTime() - commentTime.getTime();
  const diffSeconds = Math.floor(diffMs / 1000);
  const diffMinutes = Math.floor(diffSeconds / 60);
  const diffHours = Math.floor(diffMinutes / 60);
  const diffDays = Math.floor(diffHours / 24);

  if (diffSeconds < 60) {
    return 'just now';
  } else if (diffMinutes < 60) {
    return `${diffMinutes}m ago`;
  } else if (diffHours < 24) {
    return `${diffHours}h ago`;
  } else if (diffDays < 7) {
    return `${diffDays}d ago`;
  } else {
    return formatDate(commentTime);
  }
}

export default function CommentsList({
  comments,
  isLoading,
  hasMore,
  onLoadMore,
}: CommentsListProps) {
  const colors = useThemeColors();

  if (isLoading && comments.length === 0) {
    return (
      <div
        style={{
          display: 'flex',
          justifyContent: 'center',
          alignItems: 'center',
          padding: spacing.lg,
          color: colors.text.secondary,
          fontSize: typography.sizes.sm,
        }}
      >
        <Icon
          name={IconName.LOADER}
          size="sm"
          style={{ marginRight: spacing.xs }}
        />
        Loading comments...
      </div>
    );
  }

  if (comments.length === 0) {
    return null;
  }

  return (
    <div
      style={{
        display: 'flex',
        flexDirection: 'column',
        gap: spacing.md,
      }}
    >
      {comments.map(comment => (
        <div
          key={comment.id}
          data-comment-id={comment.id}
          className="comment-item group"
          style={{
            display: 'flex',
            gap: spacing.sm,
            transition: 'background-color 0.3s ease',
          }}
        >
          {/* User avatar placeholder */}
          <div
            style={{
              width: sizing.icon.xl,
              height: sizing.icon.xl,
              borderRadius: '50%',
              backgroundColor: colors.primary,
              color: colors.text.inverse,
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              fontSize: typography.sizes.sm,
              fontWeight: 600,
              flexShrink: 0,
            }}
          >
            {comment.display_name.charAt(0).toUpperCase()}
          </div>

          {/* Comment content */}
          <div style={{ flex: 1, minWidth: 0 }}>
            {/* User info and timestamp */}
            <div
              style={{
                display: 'flex',
                alignItems: 'center',
                gap: spacing.xs,
                marginBottom: 'calc(4px * var(--sizing-multiplier, 1))',
              }}
            >
              <div
                style={{
                  display: 'flex',
                  alignItems: 'baseline',
                  gap: spacing.xs,
                }}
              >
                <span
                  style={{
                    fontSize: typography.sizes.sm,
                    fontWeight: 600,
                    color: colors.text.primary,
                  }}
                >
                  {comment.display_name}
                </span>
                <span
                  style={{
                    fontSize: typography.sizes.xs,
                    color: colors.text.secondary,
                  }}
                >
                  @{comment.username}
                </span>
              </div>
              <div
                style={{
                  marginLeft: 'auto',
                  display: 'flex',
                  alignItems: 'center',
                  gap: spacing.xs,
                }}
              >
                <span
                  style={{
                    fontSize: typography.sizes.xs,
                    color: colors.text.secondary,
                  }}
                >
                  {formatRelativeTime(comment.created_at)}
                </span>
              </div>
            </div>

            {/* Comment text with highlighted mentions */}
            <div
              style={{
                fontSize: typography.sizes.sm,
                color: colors.text.primary,
                lineHeight: typography.lineHeights.normal,
                whiteSpace: 'pre-wrap',
                wordBreak: 'break-word',
              }}
            >
              {parseCommentContent(comment.content, colors.primary)}
            </div>

            {/* Media attachments (if any) */}
            {comment.media_attachments &&
              comment.media_attachments.length > 0 && (
                <div
                  style={{
                    marginTop: spacing.xs,
                    display: 'flex',
                    flexWrap: 'wrap',
                    gap: spacing.xs,
                  }}
                >
                  {comment.media_attachments.map((url, index) => (
                    <a
                      key={index}
                      href={url}
                      target="_blank"
                      rel="noopener noreferrer"
                      style={{
                        padding: `calc(4px * var(--sizing-multiplier, 1)) ${spacing.xs}`,
                        fontSize: typography.sizes.xs,
                        color: colors.primary,
                        backgroundColor: colors.hover.background,
                        borderRadius:
                          'calc(4px * (1 + (var(--sizing-multiplier, 1) - 1) * 0.5))',
                        textDecoration: 'none',
                        display: 'flex',
                        alignItems: 'center',
                        gap: 'calc(4px * var(--sizing-multiplier, 1))',
                      }}
                    >
                      <Icon name={IconName.INFO} size="sm" />
                      Attachment {index + 1}
                    </a>
                  ))}
                </div>
              )}
          </div>
        </div>
      ))}

      {/* Load more button */}
      {hasMore && onLoadMore && (
        <div style={{ display: 'flex', justifyContent: 'center' }}>
          <AsyncButton
            onClick={onLoadMore}
            isLoading={isLoading}
            variant="outline"
            size="sm"
            icon={IconName.CHEVRON_DOWN}
            loadingText="Loading..."
          >
            Load more comments
          </AsyncButton>
        </div>
      )}
    </div>
  );
}
